import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, Calendar, User, X } from 'lucide-react';
import DentalChart from '../../components/DentalChart';
import PedoDentalChart from '../../components/PedoDentalChart';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTreatments = () => {
  const { t } = useLanguage();
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isPedo = adminUser.role === 'pedo';
  const ChartComponent = isPedo ? PedoDentalChart : DentalChart;
  const [treatments, setTreatments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState(null);
  const formRef = useRef(null);
  const defaultDoctor = isPedo ? 'Dr. Hanadi Safadi' : '';
  const [formData, setFormData] = useState({
    patient_id: '', date: new Date().toISOString().split('T')[0], treatment_type: '', doctor: defaultDoctor, notes: '', cost: '', teeth: []
  });

  useEffect(() => { fetchTreatments(); fetchPatients(); }, []);

  const fetchTreatments = async () => {
    try {
      const res = await axios.get(`${API}/admin/treatments`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      setTreatments(res.data);
    } catch (e) { toast.error('Failed to load treatments'); }
    finally { setLoading(false); }
  };

  const fetchPatients = async () => {
    try {
      const res = await axios.get(`${API}/admin/patients`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      setPatients(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const payload = { ...formData, cost: parseFloat(formData.cost) };
      if (editingTreatment) {
        await axios.put(`${API}/admin/treatments/${editingTreatment.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Treatment updated');
      } else {
        await axios.post(`${API}/admin/treatments`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Treatment added');
      }
      closeForm();
      fetchTreatments();
    } catch (e) { toast.error('Failed to save treatment'); }
  };

  const handleEdit = (t) => {
    setEditingTreatment(t);
    setFormData({ patient_id: t.patient_id, date: t.date, treatment_type: t.treatment_type, doctor: t.doctor, notes: t.notes, cost: t.cost.toString(), teeth: t.teeth || [] });
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this treatment?')) return;
    try {
      await axios.delete(`${API}/admin/treatments/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      toast.success('Deleted');
      setTreatments(treatments.filter(t => t.id !== id));
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleToggleTooth = (num) => {
    setFormData(prev => ({ ...prev, teeth: prev.teeth.includes(num) ? prev.teeth.filter(t => t !== num) : [...prev.teeth, num] }));
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingTreatment(null);
    setFormData({ patient_id: '', date: new Date().toISOString().split('T')[0], treatment_type: '', doctor: defaultDoctor, notes: '', cost: '', teeth: [] });
  };

  // Compute treated teeth context for the selected patient
  const teethContext = useMemo(() => {
    if (!formData.patient_id) return { treatedTeethMap: {}, multiTreatTeeth: [] };
    const patientTreatments = treatments.filter(t =>
      t.patient_id === formData.patient_id && (!editingTreatment || t.id !== editingTreatment.id)
    );
    const toothCount = {};
    const toothInfos = {};
    patientTreatments.forEach(t => {
      (t.teeth || []).forEach(tooth => {
        toothCount[tooth] = (toothCount[tooth] || 0) + 1;
        if (!toothInfos[tooth]) toothInfos[tooth] = [];
        toothInfos[tooth].push(`${t.treatment_type} (${new Date(t.date).toLocaleDateString()})`);
      });
    });
    const treatedTeethMap = {};
    const multiTreatTeeth = [];
    Object.entries(toothInfos).forEach(([tooth, infos]) => {
      const num = parseInt(tooth);
      treatedTeethMap[num] = infos.join('\n');
      if (infos.length > 1) multiTreatTeeth.push(num);
    });
    return { treatedTeethMap, multiTreatTeeth };
  }, [formData.patient_id, treatments, editingTreatment]);

  // Compute overview teeth context for the entire treatments list (per patient)
  const overviewTeethContext = useMemo(() => {
    const toothCount = {};
    const toothInfos = {};
    treatments.forEach(t => {
      (t.teeth || []).forEach(tooth => {
        toothCount[tooth] = (toothCount[tooth] || 0) + 1;
        if (!toothInfos[tooth]) toothInfos[tooth] = [];
        toothInfos[tooth].push(`${t.patient_name || 'Unknown'}: ${t.treatment_type} (${new Date(t.date).toLocaleDateString()})`);
      });
    });
    const treatedTeethMap = {};
    const multiTreatTeeth = [];
    Object.entries(toothInfos).forEach(([tooth, infos]) => {
      const num = parseInt(tooth);
      treatedTeethMap[num] = infos.join('\n');
      if (infos.length > 1) multiTreatTeeth.push(num);
    });
    return { treatedTeethMap, multiTreatTeeth };
  }, [treatments]);

  const openForm = () => {
    closeForm();
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const filtered = treatments.filter(t =>
    t.treatment_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.doctor.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.patient_name && t.patient_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-treatments-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.treatmentManagement}</h1>
          <p className="text-[#6B7280]">{t.manageTreatments}</p>
        </div>
        <button onClick={openForm} className="bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold inline-flex items-center transition-all shadow-lg" data-testid="add-treatment-button">
          <Plus className="w-5 h-5 mr-2" /> {t.addTreatment}
        </button>
      </div>

      {/* INLINE FORM */}
      {showForm && (
        <div ref={formRef} className="bg-white rounded-xl border-2 border-[#0F4C5C]/20 shadow-lg p-6 mb-8" data-testid="treatment-form">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-[#1F2937]" style={{ fontFamily: 'Playfair Display, serif' }}>{editingTreatment ? t.editTreatment : t.addNewTreatment}</h3>
            <button onClick={closeForm} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-form"><X className="w-5 h-5 text-[#6B7280]" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.patient}</label>
              <select value={formData.patient_id} onChange={e => setFormData({ ...formData, patient_id: e.target.value })} required disabled={!!editingTreatment} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="treatment-patient-select">
                <option value="">{t.selectPatient}</option>
                {patients.map(p => <option key={p.id} value={p.id}>{p.name} - {p.email}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.date}</label>
                <input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="treatment-date-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.cost} ($)</label>
                <input type="number" step="0.01" value={formData.cost} onChange={e => setFormData({ ...formData, cost: e.target.value })} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="treatment-cost-input" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.treatmentType}</label>
              <input type="text" value={formData.treatment_type} onChange={e => setFormData({ ...formData, treatment_type: e.target.value })} required placeholder="e.g., Root Canal, Cleaning, Filling" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="treatment-type-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.doctor}</label>
              {isPedo ? (
                <input type="text" value="Dr. Hanadi Safadi" readOnly className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-[#1F2937] cursor-not-allowed" data-testid="treatment-doctor-readonly" />
              ) : (
                <select value={formData.doctor} onChange={e => setFormData({ ...formData, doctor: e.target.value })} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="treatment-doctor-select">
                  <option value="">{t.selectDoctor}</option>
                  <option value="Dr. Ali Younes">Dr. Ali Younes</option>
                  <option value="Dr. Bilal Younes">Dr. Bilal Younes</option>
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.selectTeeth}</label>
              {formData.patient_id && (Object.keys(teethContext.treatedTeethMap).length > 0 || teethContext.multiTreatTeeth.length > 0) && (
                <div className="flex flex-wrap gap-3 mb-3" data-testid="chart-legend">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="w-3 h-3 rounded-sm" style={{ background: '#0F4C5C' }}></span>
                    <span className="text-[#6B7280]">Selected</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="w-3 h-3 rounded-sm" style={{ background: '#E07A5F' }}></span>
                    <span className="text-[#6B7280]">Previously Treated</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="w-3 h-3 rounded-sm" style={{ background: '#22C55E' }}></span>
                    <span className="text-[#6B7280]">Multiple Treatments</span>
                  </div>
                </div>
              )}
              <div className="border border-gray-200 rounded-lg p-4 bg-[#F9FAFB] space-y-4">
                {isPedo && (
                  <div>
                    <p className="text-xs font-semibold text-purple-600 mb-2 uppercase tracking-wide">Primary Teeth (Deciduous)</p>
                    <PedoDentalChart selectedTeeth={formData.teeth} onToggleTooth={handleToggleTooth} interactive={true} treatedTeethMap={teethContext.treatedTeethMap} multiTreatTeeth={teethContext.multiTreatTeeth} />
                  </div>
                )}
                <div>
                  {isPedo && <p className="text-xs font-semibold text-[#0F4C5C] mb-2 uppercase tracking-wide">Permanent Teeth (Adult)</p>}
                  <DentalChart selectedTeeth={formData.teeth} onToggleTooth={handleToggleTooth} interactive={true} treatedTeethMap={teethContext.treatedTeethMap} multiTreatTeeth={teethContext.multiTreatTeeth} />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.notes}</label>
              <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} required rows="3" placeholder="Treatment details..." className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="treatment-notes-input" />
            </div>
            <div className="flex space-x-4 pt-2">
              <button type="button" onClick={closeForm} className="flex-1 px-6 py-3 border-2 border-[#0F4C5C] text-[#0F4C5C] rounded-full font-semibold hover:bg-[#0F4C5C]/5 transition-colors" data-testid="cancel-treatment-button">{t.cancel}</button>
              <button type="submit" className="flex-1 px-6 py-3 bg-[#0F4C5C] text-white rounded-full font-semibold hover:bg-[#0F4C5C]/90 transition-colors" data-testid="save-treatment-button">{editingTreatment ? t.updateTreatment : t.addTreatment}</button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
          <input type="text" placeholder={t.search} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center"><p className="text-[#6B7280]">{searchTerm ? t.noResults : t.noTreatmentsYet}</p></div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F9FAFB] border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.date}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.patient}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.treatmentType}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.doctor}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.teeth}</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#1F2937]">{t.cost}</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#1F2937]">{t.actions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(t => (
                  <tr key={t.id} className="hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-6 py-4"><div className="flex items-center text-sm text-[#6B7280]"><Calendar className="w-4 h-4 mr-2" />{new Date(t.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div></td>
                    <td className="px-6 py-4"><div className="font-medium text-[#1F2937]">{t.patient_name || 'Unknown'}</div></td>
                    <td className="px-6 py-4"><span className="font-medium text-[#1F2937]">{t.treatment_type}</span></td>
                    <td className="px-6 py-4"><div className="flex items-center text-sm text-[#6B7280]"><User className="w-4 h-4 mr-2" />{t.doctor}</div></td>
                    <td className="px-6 py-4">{t.teeth && t.teeth.length > 0 ? (<div className="flex flex-wrap gap-1">{t.teeth.sort((a,b)=>a-b).map(n => (<span key={n} className={`inline-block text-xs font-semibold px-1.5 py-0.5 rounded ${overviewTeethContext.multiTreatTeeth.includes(n) ? 'bg-green-100 text-green-700' : 'bg-[#E07A5F]/15 text-[#E07A5F]'}`} data-testid={`tooth-badge-${t.id}-${n}`}>#{n}{overviewTeethContext.multiTreatTeeth.includes(n) ? ' (x' + (treatments.filter(tr => (tr.teeth || []).includes(n)).length) + ')' : ''}</span>))}</div>) : '-'}</td>
                    <td className="px-6 py-4 text-right"><span className="text-sm font-semibold">${t.cost.toFixed(2)}</span></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center space-x-2">
                        <button onClick={() => handleEdit(t)} className="p-2 text-[#0F4C5C] hover:bg-[#0F4C5C]/10 rounded-lg" data-testid={`edit-treatment-${t.id}`}><Edit className="w-5 h-5" /></button>
                        <button onClick={() => handleDelete(t.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-treatment-${t.id}`}><Trash2 className="w-5 h-5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTreatments;
